import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Brain, Sparkles, CheckCircle, XCircle, Wand2 } from 'lucide-react';
import type { AiAssessmentSuggestion } from '../../lib/mockAiAssessment';

type AiAssessmentAssistPanelProps = {
  suggestion: AiAssessmentSuggestion;
  onApplyAll: () => void;
  onApplyRecommendationOnly: () => void;
  disabled?: boolean;
};

export function AiAssessmentAssistPanel({
  suggestion,
  onApplyAll,
  onApplyRecommendationOnly,
  disabled,
}: AiAssessmentAssistPanelProps) {
  return (
    <Card className="border-[rgba(26,86,219,0.25)] bg-gradient-to-br from-[rgba(26,86,219,0.06)] to-[var(--gold-pale)] shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(26,86,219,0.12)]">
              <Brain className="h-5 w-5 text-[var(--royal)]" />
            </div>
            <div>
              <CardTitle className="text-[var(--navy)] font-display flex items-center gap-2">
                AI-assisted insights
                <Sparkles className="h-4 w-4 text-[var(--gold)]" />
              </CardTitle>
              <CardDescription className="text-[var(--navy)]/70 mt-1">
                Suggestions are starting points only. You always make the final assessment and can change any field.
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="border-[var(--royal)] text-[var(--royal)]">
            {suggestion.confidenceLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-white/80 border-[rgba(201,168,76,0.35)]">
          <AlertDescription className="text-[var(--navy)] text-sm">
            Backend model integration is not connected yet. Text below is a <strong>preview pattern</strong> for layout and
            workflow; replace with live AI output when the API is ready.
          </AlertDescription>
        </Alert>

        <div>
          <p className="text-sm font-medium text-[var(--navy)] mb-2">Suggested decision</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              className={`rounded-lg border-2 p-4 flex gap-3 ${
                suggestion.decision === 'has-potential'
                  ? 'border-[var(--royal)] bg-[rgba(26,86,219,0.08)]'
                  : 'border-slate-200 bg-white/60'
              }`}
            >
              {suggestion.decision === 'has-potential' ? (
                <CheckCircle className="w-6 h-6 text-[var(--royal)] shrink-0" />
              ) : (
                <CheckCircle className="w-6 h-6 text-slate-300 shrink-0" />
              )}
              <div>
                <div className="font-medium text-slate-900">Has potential</div>
                <p className="text-xs text-slate-600 mt-1">AI leans toward protectable subject matter.</p>
              </div>
            </div>
            <div
              className={`rounded-lg border-2 p-4 flex gap-3 ${
                suggestion.decision === 'no-potential'
                  ? 'border-red-400 bg-red-50/80'
                  : 'border-slate-200 bg-white/60'
              }`}
            >
              {suggestion.decision === 'no-potential' ? (
                <XCircle className="w-6 h-6 text-red-600 shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-slate-300 shrink-0" />
              )}
              <div>
                <div className="font-medium text-slate-900">No potential</div>
                <p className="text-xs text-slate-600 mt-1">Use if you disagree with the suggestion above.</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-[var(--navy)] mb-2">Suggested Intellectual Property types</p>
          <div className="flex flex-wrap gap-2">
            {suggestion.ipTypes.map((t) => (
              <Badge key={t} className="bg-[var(--gold)]/25 text-[var(--navy)] border border-[rgba(201,168,76,0.4)]">
                {t}
              </Badge>
            ))}
          </div>
        </div>

        <Separator />

        <div>
          <p className="text-sm font-medium text-[var(--navy)] mb-2">Rationale (preview)</p>
          <p className="text-sm text-slate-700 leading-relaxed rounded-md bg-white/70 border border-slate-200/80 p-3">
            {suggestion.rationale}
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-[var(--navy)] mb-2">Draft recommendation text</p>
          <p className="text-sm text-slate-700 leading-relaxed rounded-md bg-white/70 border border-slate-200/80 p-3 whitespace-pre-wrap">
            {suggestion.recommendation}
          </p>
        </div>

        {suggestion.criteriaNotes.length > 0 && (
          <div>
            <p className="text-sm font-medium text-[var(--navy)] mb-2">Notes by criterion (preview)</p>
            <ul className="space-y-2">
              {suggestion.criteriaNotes.map((n) => (
                <li key={n.criterionId} className="text-sm text-slate-700 pl-3 border-l-2 border-[var(--gold)]">
                  <span className="text-slate-500 text-xs uppercase tracking-wide">{n.criterionId.replace(/-/g, ' ')} — </span>
                  {n.note}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            type="button"
            className="flex-1 gap-2"
            onClick={onApplyAll}
            disabled={disabled}
          >
            <Wand2 className="h-4 w-4" />
            Apply all suggestions to form
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={onApplyRecommendationOnly} disabled={disabled}>
            Insert draft text only
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
